import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.muted = true;
    videoElement.defaultMuted = true;
    videoElement.load();

    const playVideo = () => {
      if (videoElement.paused) {
        const playPromise = videoElement.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {});
        }
      }
    };

    // Attempt autoplay immediately
    playVideo();

    // Listen for canplay / loadeddata
    videoElement.addEventListener('canplay', playVideo, { once: true });
    videoElement.addEventListener('loadeddata', playVideo, { once: true });

    // Fallback: On iOS Low Power Mode, start video on first touch, scroll, or click anywhere on the page
    const onFirstInteraction = () => {
      playVideo();
      if (!videoElement.paused) {
        window.removeEventListener('touchstart', onFirstInteraction);
        window.removeEventListener('scroll', onFirstInteraction);
        window.removeEventListener('click', onFirstInteraction);
      }
    };

    window.addEventListener('touchstart', onFirstInteraction, { passive: true });
    window.addEventListener('scroll', onFirstInteraction, { passive: true });
    window.addEventListener('click', onFirstInteraction, { passive: true });
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
